import { c as _c } from "react/compiler-runtime";
import type { BigIntish, ChainId, MarketId } from '@morpho-org/blue-sdk';
import { toBigInt } from '@morpho-org/dapp-core';
import { useAutoDeleverage } from '../hooks/useAutoDeleverage';
import { useIsAutoDeleverageEnabled } from '../hooks/useIsAutoDeleverageEnabled';

/**
 * Hook that returns protected LLTV when auto-deleverage is authorized, otherwise base LLTV.
 * @param params - Parameters object
 * @param params.lltv - Base liquidation LTV
 * @param params.marketId - Market identifier
 * @param params.chainId - Chain ID of the market. Required to avoid silently
 *   reading from the wrong chain in multi-chain contexts (e.g. Dashboard).
 * @returns Protected LLTV if auto-deleverage authorized, otherwise base LLTV
 */
export const useProtectedLltv = t0 => {
  const $ = _c(4);
  const {
    lltv,
    marketId,
    chainId
  } = t0;
  const isAutoDeleverageEnabled = useIsAutoDeleverageEnabled(chainId);
  const {
    status: autoDeleverageStatus,
    preLltv
  } = useAutoDeleverage(marketId, chainId);
  if (!isAutoDeleverageEnabled) {
    let t1;
    if ($[0] !== lltv) {
      t1 = toBigInt(lltv);
      $[0] = lltv;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (autoDeleverageStatus === "authorized") {
    return preLltv;
  }
  let t1;
  if ($[2] !== lltv) {
    t1 = toBigInt(lltv);
    $[2] = lltv;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};

/**
 * Whether the per-position AutoDeleverage authorization is still unresolved — the read that decides
 * `preLltv` vs the raw market LLTV is `loading` or errored. While pending, {@link useProtectedLltv}
 * optimistically returns the raw LLTV, so any health-sensitive bound derived from it (e.g. the manual
 * borrow/withdraw ceiling) is NOT yet trustworthy and the flow must be blocked until this is false.
 * Returns `false` when AutoDeleverage isn't available on the chain (nothing to wait on).
 * @param params.marketId - Market identifier.
 * @param params.chainId - Chain ID of the market (must be explicit — no silent target-chain fallback).
 */
export const useIsProtectedLltvPending = t0 => {
  const {
    marketId,
    chainId
  } = t0;
  const isAutoDeleverageEnabled = useIsAutoDeleverageEnabled(chainId);
  const {
    status
  } = useAutoDeleverage(marketId, chainId);
  return isAutoDeleverageEnabled && (status === "loading" || status === "error");
};